import { CellContext } from "@tanstack/react-table";
import { string } from "prop-types";
import { IPossibleProcessInstanceListColumn } from "shared/model/Table";

export { AgentGroup } from "../../shared/model/AgentGroup";
export { ProcessInstanceStateFilter } from "../../processList/model/ProcessInstanceStateFilter";

export interface ProcessInstanceListData {
    instances: ProcessInstanceData[];
    hasMoreInstances: boolean;
}

export interface ProcessInstanceData {
    executionId: string;
    id: number;
    exception: string;
    startTimeUtc: string;
    endTimeUtc: string;
    promotedColumns: PromotedColumnData[];
    acknowledgedUtc: string;
    comment: string;
    state: string;
    triggerName: string;
    agentName: string;
    environmentName: string;
    agentGroupName: string;
    processVersion: number;
    triggerRequest: string;
    triggerResponses: string;
    userName: string;
    parentName?: string;
    parentId?: number;
    processGuid: string;
    hasLoggedSteps: boolean;
    dehydrationState?: DehydrationState;
    correlationId?: string
}

export interface PromotedColumnData {
    name: string;
    value: string;
}

export interface ProcessInstanceListProps {
    instances: ProcessInstanceData[];
    selectedInstances: number[];
    availablePromotedColumns: string[];
    processNameLookup: { [guid: string]: string };
    isSubprocess: boolean;
    isRefetching: boolean;
    processGuids: string[];
    onSelectInstances(instanceIds: number[]): void;
    onDeselectInstances(instanceIDs: number[]): void;
}

export interface PromotedColumnData {
    name: string;
    value: string;
}

export interface ExecutionStateProps {
    state: string,
    dehydrationState?: DehydrationState
}

type ListPromotedColumn = Record<string, unknown>;

export type ListColumnType = ListPromotedColumn & {
    [ListColumn.id]: number;
    [ListColumn.executionState]: ExecutionStateProps;
    [ListColumn.name]: string;
    [ListColumn.processVersion]: number;
    [ListColumn.startTimeUtc]: string;
    [ListColumn.endTimeUtc]: string;
    [ListColumn.duration]?: string;
    [ListColumn.userName]: string;
    [ListColumn.triggerName]: string;
    [ListColumn.agentName]: string;
    [ListColumn.parentName]?: string,
    [ListColumn.parentId]?: number;
    [ListColumn.exception]: string;
    [ListColumn.triggerRequest]: string;
    [ListColumn.triggerResponses]: string;
    [ListColumn.comment]: string;
    [ListColumn.environmentName]: string;
    [ListColumn.agentGroupName]: string;
    [ListColumn.acknowledgedUtc]: string | null;
    [ListColumn.hasLoggedSteps]: boolean;
    [ListColumn.correlationId]?: string;
}

export enum ListColumn {
    id = "id",
    executionState = "executionState",
    name = "name",
    processVersion = "processVersion",
    startTimeUtc = "startTimeUtc",
    endTimeUtc = "endTimeUtc",
    duration = "duration",
    userName = "userName",
    triggerName = "triggerName",
    agentName = "agentName",
    parentName = "parentName",
    parentId = "parentId",
    exception = "exception",
    triggerRequest = "triggerRequest",
    triggerResponses = "triggerResponses",
    comment = "comment",
    environmentName = "environmentName",
    agentGroupName = "agentGroupName",
    acknowledgedUtc = "acknowledgedUtc",
    hasLoggedSteps = "hasLoggedSteps",
    correlationId = "correlationId"
}

export enum EInstanceCommentType {
    Acknowledge,
    Comment
}

export interface InstanceComment {
    modifier: string;
    message: string;
    modified: string;
    type: EInstanceCommentType;
}

export const possibleProcessInstanceColumns: IPossibleProcessInstanceListColumn[] = [
    { column: ListColumn.endTimeUtc, label: FRENDS.Localizations.EndTime },
    { column: ListColumn.duration, label: FRENDS.Localizations.Duration },
    { column: ListColumn.userName, label: FRENDS.Localizations.Username },
    { column: ListColumn.triggerName, label: FRENDS.Localizations.Trigger },
    { column: ListColumn.agentName, label: FRENDS.Localizations.Agent },
    { column: ListColumn.parentName, label: FRENDS.Localizations.ParentProcess, subProcessOnly: true },
    { column: ListColumn.exception, label: FRENDS.Localizations.ErrorInfo },
    { column: ListColumn.triggerRequest, label: FRENDS.Localizations.ProcessParameters },
    { column: ListColumn.triggerResponses, label: FRENDS.Localizations.ProcessReturnValues },
    { column: ListColumn.comment, label: FRENDS.Localizations.Comment },
    { column: ListColumn.hasLoggedSteps, label: "Has logged steps" },
    { column: ListColumn.correlationId, label: "Correlation ID" }
];

export interface IInstanceDetailsLinkWrapperProps<T = string> extends React.PropsWithChildren<CellContext<ListColumnType, T>> {
    isSubprocess: boolean;
    isParentColumn?: boolean;
    processName?: string;
}

export enum DehydrationState {
    Dehydrated = "Dehydrated",
    Hydrated = "Hydrated"
}